import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Icon,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@material-ui/core';
import { getQuoteProductSubscriptionOptionsApi } from 'api';
import {
  configureTreatmentPlan,
  setFinalPageData,
} from 'redux/actions/quote';
import { CalendarIcon } from 'components/CustomSVG';
import { useStyles } from '../styles';
import QuoteSummaryTableRow from './QuoteSummaryTableRow';
import QuoteSummaryGroupTableRow from './QuoteSummaryGroupTableRow';


const QuoteSummaryTable = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { id } = useParams<{id?: string}>();

  const {
    loadingPatientsGroupCart,
    customerPushCart,
    quotePlan,
    patientsGroupCart,
    subscriptionOptionsBySku,
  } = useSelector((state: IReduxState) => state.quote);

  const [selectedItemsIds, setSelectedItemsIds] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const isPatientGroupActive = location.pathname.includes('/quoting/group/');

  const { profile } = useSelector((state: IReduxState) => state.user);
  const permissions = profile?.config?.permissions || {};

  useEffect(() => {
    if (isPatientGroupActive) {
      setSummaryData(patientsGroupCart);
    } else {
      setSummaryData(customerPushCart.selectedItems);
    }

    return () => {
      setSelectedItemsIds([]);
      setSummaryData([]);
    };
  }, [customerPushCart, patientsGroupCart, loadingPatientsGroupCart]);

  useEffect(() => {
    if (patientsGroupCart.length) {
      patientsGroupCart.map((item) => {
        const {sku} = item;

        if (!subscriptionOptionsBySku?.[sku] && sku) {
          getQuoteProductSubscriptionOptionsApi({ dispatch, sku });
        }
      });
    }
  }, [patientsGroupCart]);

  const handleItemSelect = (itemId, isAdded) => {
    const updatedSelectedItemsIds = isAdded
      ? [...selectedItemsIds, itemId]
      : selectedItemsIds.filter((id) => id !== itemId);

    setSelectedItemsIds(updatedSelectedItemsIds);

    const idKey = isPatientGroupActive ? 'sku' : '_id';
    const selectedItems = summaryData.filter((item) =>
      updatedSelectedItemsIds.includes(item[idKey])
    );

    dispatch(setFinalPageData(selectedItems, 30));
  };

  const handleSelectAll = ({ target: { checked } }) => {
    const allItemIds = summaryData.map((item) =>
      isPatientGroupActive ? item.sku : item._id
    );

    const updatedSelectedItemsIds = checked ? allItemIds : [];

    setSelectedItemsIds(updatedSelectedItemsIds);

    const idKey = isPatientGroupActive ? 'sku' : '_id';
    const selectedItemsData = summaryData.filter((item) =>
      updatedSelectedItemsIds.includes(item[idKey])
    );

    dispatch(setFinalPageData(selectedItemsData, 30));
  };

  const handleEdit = () => {
    if (isPatientGroupActive) {
      history.push({ pathname: `/quoting/group/${id}/quote-workspace` });
    } else {
      history.push(`/quoting/profile/${id}/quote-workspace`);
    }
  };

  const handleStartTreatmentPlan = () => {
    if (!quotePlan.length) {
      dispatch(configureTreatmentPlan());
    }

    history.push({ pathname: `/quoting/profile/${id}/quote-workspace/plan` });
  };

  return (
    <Card className="p-4 root-card">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" gutterBottom>
            Items ({summaryData.length})
          </Typography>
        </Box>
        <Box>
          {permissions?.quoteTreatmentPlan && !isPatientGroupActive &&
            <Button
              variant="outlined"
              className={`${classes.quoteSummaryCTAButton} mr-2`}
              onClick={handleStartTreatmentPlan}
            >
              <CalendarIcon className="mr-1" />
              Configure Plan
            </Button>
          }
          <Button
            variant="outlined"
            className={classes.quoteSummaryCTAButton}
            onClick={handleEdit}
          >
            <Icon fontSize="small" className="mr-1">
              edit
            </Icon>
            Edit
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box display="flex" alignItems="center" my={1}>
        <Checkbox
          checked={selectedItemsIds.length === summaryData.length}
          color="primary"
          onChange={handleSelectAll}
        />
        <Typography gutterBottom className="mb-0">
          Select All Items
        </Typography>
      </Box>
      <TableContainer>
        <Table className={classes.selectedItemsTable}>
          <TableBody className={classes.quoteSummaryTable}>
            {summaryData.map((item) => (
              isPatientGroupActive
                ? <QuoteSummaryGroupTableRow key={item.sku} item={item} selectedItems={selectedItemsIds} handleItemSelect={handleItemSelect} />
                : <QuoteSummaryTableRow key={item._id} item={item} selectedItems={selectedItemsIds} handleItemSelect={handleItemSelect} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default QuoteSummaryTable;
